import { SurveyTheme as SurveyThemeType, SurveyContainerStyle, CommonStyle } from '@sm/webassets/SurveyTheme/types';
import { SurveyThemeVersion } from '~lib/generatedGqlTypes';

type LayoutOptionType = Required<SurveyThemeType['layout']>;
export type LayoutName = LayoutOptionType | '';
export const isPartial = (layoutName: LayoutName = ''): boolean => {
  return layoutName.includes('PARTIAL');
};
export const isCenter = (layoutName: LayoutName = ''): boolean => {
  return layoutName.includes('CENTER');
};
export const isLeft = (layoutName: LayoutName = ''): boolean => {
  return layoutName.includes('LEFT');
};
export const isRight = (layoutName: LayoutName = ''): boolean => {
  return layoutName.includes('RIGHT');
};
export const isLeftOrRight = (layoutName: LayoutName = ''): boolean => {
  return isLeft(layoutName) || isRight(layoutName);
};
export const isPartialSide = (layoutName: LayoutName = ''): boolean => {
  return isPartial(layoutName) && isLeftOrRight(layoutName);
};
export const isPartialLeft = (layoutName: LayoutName = ''): boolean => {
  return isPartial(layoutName) && isLeft(layoutName);
};
export const isPartialRight = (layoutName: LayoutName = ''): boolean => {
  return isPartial(layoutName) && isRight(layoutName);
};
export const isPartialHalf = (layoutName: LayoutName = ''): boolean => {
  return isPartial(layoutName) && layoutName.includes('HALF');
};
export const isPartialThird = (layoutName: LayoutName = ''): boolean => {
  return isPartial(layoutName) && layoutName.includes('THIRD');
};
export const isBanner = (layoutName: LayoutName = ''): boolean => {
  return layoutName.includes('BANNER');
};
export const isFull = (layoutName: LayoutName = ''): boolean => {
  return layoutName.includes('FULL');
};
export const isFullLeftOrRight = (layoutName: LayoutName = ''): boolean => {
  return isFull(layoutName) && !isCenter(layoutName);
};

export const getThemeLayoutType = (layoutName: LayoutName = ''): Record<string, boolean> => {
  return {
    isPartial: isPartial(layoutName),
    isCenter: isCenter(layoutName),
    isLeft: isLeft(layoutName),
    isRight: isRight(layoutName),
    isLeftOrRight: isLeftOrRight(layoutName),
    isPartialSide: isPartialSide(layoutName),
    isPartialLeft: isPartialLeft(layoutName),
    isPartialRight: isPartialRight(layoutName),
    isPartialHalf: isPartialHalf(layoutName),
    isPartialThird: isPartialThird(layoutName),
    isBanner: isBanner(layoutName),
    isFull: isFull(layoutName),
  };
};

// Note: Refactor for cleaner code.
export type LayoutSizeName = 'full' | 'fullLeft' | 'fullRight' | 'half' | 'third' | 'top';
export const getLayoutSize = (layoutName: LayoutName = ''): LayoutSizeName => {
  const lo = getThemeLayoutType(layoutName);
  let size: LayoutSizeName = 'full';
  if (lo.isFull && lo.isLeftOrRight) {
    if (lo.isLeft) {
      size += 'Left';
    } else {
      size += 'Right';
    }
  } else if (lo.isPartialSide && !lo.isPartialThird) {
    size = 'half';
  } else if (lo.isPartialThird) {
    size = 'third';
  } else if (lo.isBanner) {
    size = 'top';
  }
  return size as LayoutSizeName;
};

// helper to identify v2 themes
export const isV2Theme = (themeVersion: SurveyThemeVersion | undefined): boolean => {
  return themeVersion === 'V2';
};

export const isFullBleed = (surveyContainer: SurveyContainerStyle | undefined): boolean => {
  return surveyContainer?.containerStyle === 'FullWidth';
};

export const isOQAATFullBleed = (surveyContainer: SurveyContainerStyle | undefined, isOQAAT: boolean): boolean => {
  return isOQAAT || isFullBleed(surveyContainer);
};

export const FULL_BLEED_CSS = {
  position: 'relative',
  width: '100vw',
  left: '50%',
  right: '50%',
  marginLeft: '-50vw',
  marginRight: '-50vw',
};

export const isBackgroundTiled = (surveyContainer: SurveyContainerStyle | undefined): boolean => {
  // V2 theme backgrounds are tiled by default, so we check to see if was specified to 'cover' instead
  return surveyContainer?.backgroundSize !== 'cover' || surveyContainer?.backgroundRepeat !== 'no-repeat';
};

// Non-customizable City Lights theme constants
export const CITY_LIGHTS_PRIMARY_ACCENT_COLOR = '#3e3547';
export const CITY_LIGHTS_SURVEY_TITLE_WRAPPER_BACKGROUND_COLOR = 'rgba(74,74,74,0.5)';

export const isCityLights = (surveyTitle: CommonStyle | undefined): boolean => {
  // City Lights is the only theme that has a background image in the survey title area
  // No theme can be customized to have a background image in the survey title area like this
  return !!surveyTitle?.backgroundImage;
};

export const isTransparent = (color: string | undefined): boolean => {
  if (!color) {
    return false;
  }
  // From Wrench: https://github.com/mntv-web-experience/wrench/blob/main/packages/utils/src/getContrastColor.ts
  const rgbaMatch = color.match(/^rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*(\d+(?:\.\d+)?))?\)$/);

  if (!rgbaMatch) {
    return false;
  }

  if (rgbaMatch.length === 5) {
    const aValue = parseFloat(rgbaMatch[4]);
    return aValue === 0;
  }
  return false;
};

/**
 * The background color for layoutTopContainerV2 depends on the placement of the logo in V2 themes (above vs. inline or below the title):
 * - If the logo is placed above the title (i.e. hasLogo is true), the logo background color will be shown
 * and the background color should be logoBackgroundColor.
 * - If the logo is placed inline or below the title (i.e. hasLogo is false), the logo background color will NOT be
 * shown. Thus, the background color should match surveyTitleBackgroundColor to make the language selector blend in.
 *
 * @returns the background color that should be used for layoutTopContainerV2
 */
export const getContainerBackgroundColor = ({
  logoBackgroundColor = '#fff',
  surveyTitleBackgroundColor = '#fff',
  isLogoEnabled = false,
  isSurveyTitleEnabled = true,
  pageTitleBackgroundColor = '#fff',
}: {
  logoBackgroundColor?: string;
  surveyTitleBackgroundColor?: string;
  isLogoEnabled?: boolean;
  isSurveyTitleEnabled?: boolean;
  pageTitleBackgroundColor?: string;
}): string => {
  if (!isSurveyTitleEnabled && !isLogoEnabled) {
    return pageTitleBackgroundColor;
  }
  return isLogoEnabled ? logoBackgroundColor : surveyTitleBackgroundColor;
};
